/**
 * Controlled vocabularies used when categorising exercises.
 *
 * Everything here is intentionally flat and stable: tag values are stored in
 * the database as strings, so the labels below are only presentation.
 */

export interface TaxonomyEntry {
  value: string;
  label: string;
  /** grouping used in the filter sidebar */
  group: string;
  description?: string;
  /**
   * A broader tag this one refines, e.g. `tree-fold` under `trees`.
   *
   * Categorising 4495 exercises by reading produced 85 topic values, 39 goals and
   * 7 types that were not in these lists — the passes were told to prefer a listed
   * value and invent one only when nothing fitted, and mostly they were right to.
   * Two kinds came out of it.  Genuine refinements (`labelled-trees`,
   * `tree-properties`, `invariants`) are kept as children, so filtering by the
   * parent finds them and the sidebar can nest them.  Strict synonyms and values
   * too rare to be worth a facet row (`arrays` beside `vectors-arrays`, `sets`
   * beside `sets-relations`) are folded away by `TAG_ALIASES` instead.
   */
  parent?: string;
}

/* ------------------------------------------------------------------ topics */

export const TOPICS: TaxonomyEntry[] = [
  // --- getting started
  { value: 'intro-programming', label: 'Intro to programming', group: 'Foundations' },
  { value: 'tooling', label: 'Tooling & environment', group: 'Foundations' },
  { value: 'types-and-values', label: 'Types & values', group: 'Foundations' },
  { value: 'expressions', label: 'Expressions', group: 'Foundations' },
  { value: 'operators-precedence', label: 'Operators & precedence', group: 'Foundations' },
  { value: 'boolean-logic', label: 'Boolean logic', group: 'Foundations' },
  { value: 'numbers-arithmetic', label: 'Numbers & arithmetic', group: 'Foundations' },
  { value: 'strings-chars', label: 'Strings & characters', group: 'Foundations' },
  { value: 'floating-point', label: 'Floating point', group: 'Foundations' },
  { value: 'number-theory', label: 'Number theory', group: 'Foundations', parent: 'numbers-arithmetic' },
  { value: 'primes', label: 'Primes', group: 'Foundations', parent: 'numbers-arithmetic' },
  { value: 'gcd', label: 'Greatest common divisor', group: 'Foundations', parent: 'numbers-arithmetic' },
  { value: 'peano-numbers', label: 'Peano numbers', group: 'Foundations', parent: 'numbers-arithmetic' },

  // --- functions
  { value: 'bindings-scoping', label: 'Bindings & scoping', group: 'Functions' },
  { value: 'shadowing', label: 'Shadowing', group: 'Functions', parent: 'bindings-scoping' },
  { value: 'functions', label: 'Functions & declarations', group: 'Functions' },
  { value: 'partial-application', label: 'Partial application', group: 'Functions', parent: 'higher-order-functions' },
  { value: 'higher-order-functions', label: 'Higher-order functions', group: 'Functions' },
  { value: 'lambda-abstraction', label: 'Lambda abstraction', group: 'Functions' },
  { value: 'polymorphism', label: 'Polymorphism', group: 'Functions' },
  { value: 'type-inference', label: 'Type inference', group: 'Functions' },
  { value: 'alpha-renaming', label: 'Alpha renaming', group: 'Functions', parent: 'bindings-scoping' },
  { value: 'type-instances', label: 'Type instances', group: 'Functions', parent: 'polymorphism' },

  // --- recursion
  { value: 'recursion', label: 'Recursion', group: 'Recursion' },
  { value: 'tail-recursion', label: 'Tail recursion', group: 'Recursion', parent: 'recursion' },
  { value: 'termination-divergence', label: 'Termination & divergence', group: 'Recursion' },
  { value: 'cascaded-recursion', label: 'Cascaded / cartesian recursion', group: 'Recursion', parent: 'recursion' },
  { value: 'execution-traces', label: 'Execution traces & logs', group: 'Recursion' },
  { value: 'iteration-iter', label: 'Iteration (iter/iterup)', group: 'Recursion' },
  { value: 'accumulators', label: 'Accumulators', group: 'Recursion' },
  { value: 'mutual-recursion', label: 'Mutual recursion', group: 'Recursion', parent: 'recursion' },
  { value: 'generators', label: 'Generators', group: 'Recursion', parent: 'iteration-iter' },

  // --- data structures
  { value: 'tuples', label: 'Tuples', group: 'Data' },
  { value: 'lists', label: 'Lists', group: 'Data' },
  { value: 'list-functions', label: 'List functions', group: 'Data', parent: 'lists' },
  { value: 'fold', label: 'fold / foldl', group: 'Data' },
  { value: 'map-filter', label: 'map & filter', group: 'Data', parent: 'higher-order-functions' },
  { value: 'sorting', label: 'Sorting', group: 'Data' },
  { value: 'searching', label: 'Searching', group: 'Data' },
  { value: 'constructors-datatypes', label: 'Constructors & datatypes', group: 'Data' },
  { value: 'pattern-matching', label: 'Pattern matching', group: 'Data' },
  { value: 'options-exceptions', label: 'Options & exceptions', group: 'Data' },
  { value: 'trees', label: 'Trees', group: 'Data' },
  { value: 'binary-trees', label: 'Binary (labeled) trees', group: 'Data', parent: 'trees' },
  { value: 'tree-traversal', label: 'Tree traversal & addressing', group: 'Data', parent: 'trees' },
  { value: 'sets-relations', label: 'Sets & relations', group: 'Data' },
  { value: 'graphs', label: 'Graphs', group: 'Data' },
  { value: 'records-modules', label: 'Records, modules & signatures', group: 'Data' },
  { value: 'vectors-arrays', label: 'Vectors & arrays', group: 'Data' },
  { value: 'queues-stacks', label: 'Queues & stacks', group: 'Data' },
  { value: 'priority-queues', label: 'Priority queues', group: 'Data', parent: 'queues-stacks' },
  { value: 'association-lists', label: 'Association lists', group: 'Data', parent: 'lists' },
  { value: 'comparators', label: 'Comparators', group: 'Data', parent: 'sorting' },
  { value: 'binary-search', label: 'Binary search', group: 'Data', parent: 'searching' },
  { value: 'labelled-trees', label: 'Labelled trees', group: 'Data', parent: 'trees' },
  { value: 'tree-properties', label: 'Tree properties', group: 'Data', parent: 'trees' },
  { value: 'tree-fold', label: 'Folding over trees', group: 'Data', parent: 'trees' },
  { value: 'tree-order', label: 'Tree orders', group: 'Data', parent: 'trees' },
  { value: 'search-trees', label: 'Search trees', group: 'Data', parent: 'trees' },
  { value: 'graph-search', label: 'Graph search', group: 'Data', parent: 'graphs' },
  { value: 'graph-properties', label: 'Graph properties', group: 'Data', parent: 'graphs' },
  { value: 'modules-signatures', label: 'Module signatures', group: 'Data', parent: 'records-modules' },
  { value: 'abstract-data-types', label: 'Abstract data types', group: 'Data', parent: 'records-modules' },
  { value: 'functors', label: 'Functors', group: 'Data', parent: 'records-modules' },

  // --- maths & proofs
  { value: 'math-functions', label: 'Mathematical procedures', group: 'Maths & proofs' },
  { value: 'induction', label: 'Induction', group: 'Maths & proofs' },
  { value: 'structural-induction', label: 'Structural induction', group: 'Maths & proofs', parent: 'induction' },
  { value: 'correctness-proofs', label: 'Correctness proofs', group: 'Maths & proofs' },
  { value: 'termination-proofs', label: 'Termination proofs', group: 'Maths & proofs' },
  { value: 'quantifiers', label: 'Quantifiers & predicate logic', group: 'Maths & proofs' },
  { value: 'orders', label: 'Orders & inclusion', group: 'Maths & proofs' },
  { value: 'pigeonhole', label: 'Pigeonhole & counting', group: 'Maths & proofs' },
  { value: 'combinatorics', label: 'Combinatorics', group: 'Maths & proofs' },
  { value: 'function-induction', label: 'Function induction', group: 'Maths & proofs', parent: 'induction' },
  { value: 'invariants', label: 'Representation invariants', group: 'Maths & proofs', parent: 'correctness-proofs' },
  { value: 'proof-critique', label: 'Critiquing a proof', group: 'Maths & proofs', parent: 'correctness-proofs' },

  // --- complexity
  { value: 'complexity', label: 'Complexity', group: 'Complexity' },
  { value: 'runtime-analysis', label: 'Runtime analysis', group: 'Complexity' },
  { value: 'cost-functions', label: 'Cost & size functions', group: 'Complexity' },
  { value: 'big-o', label: 'Big-O notation', group: 'Complexity' },
  { value: 'recurrences', label: 'Recurrences', group: 'Complexity', parent: 'cost-functions' },
  { value: 'amortised-analysis', label: 'Amortised analysis', group: 'Complexity', parent: 'runtime-analysis' },
  { value: 'dynamic-programming', label: 'Dynamic programming', group: 'Complexity' },

  // --- syntax & semantics
  { value: 'abstract-syntax', label: 'Abstract syntax', group: 'Syntax & semantics' },
  { value: 'concrete-syntax', label: 'Concrete syntax', group: 'Syntax & semantics' },
  { value: 'syntax-trees', label: 'Syntax trees', group: 'Syntax & semantics' },
  { value: 'grammars-bnf', label: 'Grammars & BNF', group: 'Syntax & semantics' },
  { value: 'tokenization', label: 'Lexical syntax & tokenisation', group: 'Syntax & semantics' },
  { value: 'parsing', label: 'Parsing', group: 'Syntax & semantics' },
  { value: 'type-checking', label: 'Type checking', group: 'Syntax & semantics' },
  { value: 'type-derivation', label: 'Type derivations', group: 'Syntax & semantics' },
  { value: 'dynamic-semantics', label: 'Dynamic semantics', group: 'Syntax & semantics' },
  { value: 'evaluation-derivation', label: 'Evaluation derivations', group: 'Syntax & semantics' },
  { value: 'environments', label: 'Environments', group: 'Syntax & semantics' },
  { value: 'closures', label: 'Closures & procedures', group: 'Syntax & semantics' },
  { value: 'elaboration', label: 'Elaboration', group: 'Syntax & semantics' },
  { value: 'interpreter', label: 'Interpreter', group: 'Syntax & semantics' },
  { value: 'static-semantics', label: 'Static semantics', group: 'Syntax & semantics' },
  { value: 'inference-rules', label: 'Inference rules', group: 'Syntax & semantics', parent: 'static-semantics' },
  { value: 'well-formedness', label: 'Well-formedness', group: 'Syntax & semantics', parent: 'static-semantics' },
  { value: 'derivation', label: 'Building a derivation', group: 'Syntax & semantics' },
  { value: 'semantic-equivalence', label: 'Semantic equivalence', group: 'Syntax & semantics', parent: 'dynamic-semantics' },
  { value: 'evaluation-strategies', label: 'Evaluation strategies', group: 'Syntax & semantics', parent: 'dynamic-semantics' },
  { value: 'recursive-descent', label: 'Recursive-descent parsing', group: 'Syntax & semantics', parent: 'parsing' },
  { value: 'regular-expressions', label: 'Regular expressions', group: 'Syntax & semantics', parent: 'grammars-bnf' },

  // --- machines & memory
  { value: 'stack-machine', label: 'Stack machine', group: 'Machines' },
  { value: 'code-generation', label: 'Code generation', group: 'Machines' },
  { value: 'memory-heap', label: 'Memory & heap', group: 'Machines' },
  { value: 'references-mutability', label: 'References & mutability', group: 'Machines' },
  { value: 'imperative-programming', label: 'Imperative programming', group: 'Machines' },
  { value: 'aliasing', label: 'Aliasing', group: 'Machines', parent: 'references-mutability' },
  { value: 'physical-equality', label: 'Physical equality', group: 'Machines', parent: 'references-mutability' },
  { value: 'linked-structures', label: 'Linked structures', group: 'Machines', parent: 'references-mutability' },

  // --- misc
  { value: 'graphics-fractals', label: 'Graphics & fractals', group: 'Misc' },
  { value: 'games', label: 'Games & puzzles', group: 'Misc' },
  { value: 'exam-prep', label: 'Exam preparation', group: 'Misc' },
  { value: 'course-organisation', label: 'Course organisation', group: 'Misc' },
  { value: 'misc', label: 'Miscellaneous', group: 'Misc' },
  { value: 'debugging', label: 'Debugging', group: 'Misc' },
];

/* ----------------------------------------------------------- exercise type */

export const EXERCISE_TYPES: TaxonomyEntry[] = [
  { value: 'coding', label: 'Write code', group: 'Production', description: 'Students implement a function or program.' },
  { value: 'proof', label: 'Proof', group: 'Production', description: 'A mathematical proof has to be written down.' },
  { value: 'derivation', label: 'Derivation tree', group: 'Production', description: 'Type or evaluation derivation.' },
  { value: 'trace', label: 'Trace / execution log', group: 'Production' },
  { value: 'drawing', label: 'Draw a diagram', group: 'Production', description: 'Trees, heaps, machine states …' },
  { value: 'calculation', label: 'Calculation', group: 'Production' },
  { value: 'long-text', label: 'Long free text', group: 'Production', description: 'Essay-like answer, explanation in prose.' },
  { value: 'short-answer', label: 'Short answer', group: 'Production' },
  { value: 'creative', label: 'Creative / open ended', group: 'Production' },

  { value: 'multiple-choice', label: 'Multiple choice', group: 'Recognition' },
  { value: 'true-false', label: 'True / false', group: 'Recognition' },
  { value: 'quiz', label: 'Quiz round', group: 'Recognition' },
  { value: 'matching', label: 'Matching / assignment', group: 'Recognition' },
  { value: 'fill-in-the-gaps', label: 'Fill in the gaps', group: 'Recognition' },
  { value: 'ordering', label: 'Ordering / sorting items', group: 'Recognition' },

  { value: 'find-the-error', label: 'Find the error', group: 'Analysis' },
  { value: 'fix-wrong-solution', label: 'Correct a wrong solution', group: 'Analysis' },
  { value: 'predict-output', label: 'Predict the output', group: 'Analysis' },
  { value: 'reading-comprehension', label: 'Read & understand code', group: 'Analysis' },
  { value: 'compare-variants', label: 'Compare variants', group: 'Analysis' },
  { value: 'counterexample', label: 'Find a counterexample', group: 'Analysis' },

  { value: 'gamification', label: 'Gamified', group: 'Format', description: 'Framed as a game, contest or story.' },
  { value: 'story-flavoured', label: 'Story flavoured', group: 'Format' },
  { value: 'discussion', label: 'Group discussion', group: 'Format' },
  { value: 'pen-and-paper', label: 'Pen & paper', group: 'Format' },
  { value: 'live-coding', label: 'Live coding / at the computer', group: 'Format' },
  { value: 'warm-up', label: 'Warm-up', group: 'Format' },
  { value: 'bonus', label: 'Bonus / challenge', group: 'Format' },
  { value: 'guided-build', label: 'Guided build (staged parts)', group: 'Format' },
  { value: 'puzzle', label: 'Puzzle', group: 'Format' },
  { value: 'humorous', label: 'Humorous framing', group: 'Format', parent: 'story-flavoured' },
  { value: 'annotation', label: 'Annotate given material', group: 'Format' },
];

/* --------------------------------------------------------- learning goals */

export const LEARNING_GOALS: TaxonomyEntry[] = [
  { value: 'read-types', label: 'Read and assign types', group: 'Types' },
  { value: 'infer-types', label: 'Infer types of expressions', group: 'Types' },
  { value: 'build-type-derivation', label: 'Build a type derivation', group: 'Types' },
  { value: 'understand-polymorphism', label: 'Understand polymorphism', group: 'Types' },

  { value: 'write-recursive-function', label: 'Write a recursive function', group: 'Programming' },
  { value: 'write-tail-recursive-function', label: 'Write a tail-recursive function', group: 'Programming' },
  { value: 'use-higher-order-functions', label: 'Use higher-order functions', group: 'Programming' },
  { value: 'use-fold', label: 'Use fold to express list functions', group: 'Programming' },
  { value: 'work-with-lists', label: 'Work with lists', group: 'Programming' },
  { value: 'work-with-trees', label: 'Work with trees', group: 'Programming' },
  { value: 'work-with-tuples', label: 'Work with tuples', group: 'Programming' },
  { value: 'define-datatypes', label: 'Define own datatypes', group: 'Programming' },
  { value: 'use-pattern-matching', label: 'Use pattern matching', group: 'Programming' },
  { value: 'translate-math-to-code', label: 'Translate maths into code', group: 'Programming' },
  { value: 'implement-interpreter', label: 'Implement (part of) an interpreter', group: 'Programming' },
  { value: 'implement-parser', label: 'Implement (part of) a parser', group: 'Programming' },
  { value: 'design-datatype', label: 'Design a datatype', group: 'Programming', parent: 'define-datatypes' },
  { value: 'work-with-strings', label: 'Work with strings', group: 'Programming' },
  { value: 'work-with-constructors', label: 'Work with constructors', group: 'Programming', parent: 'define-datatypes' },
  { value: 'use-option-type', label: 'Use the option type', group: 'Programming' },
  { value: 'use-exceptions', label: 'Use exceptions', group: 'Programming' },
  { value: 'use-mutable-state', label: 'Use mutable state', group: 'Programming' },
  { value: 'use-modules', label: 'Use modules', group: 'Programming' },
  { value: 'write-comparator', label: 'Write a comparator', group: 'Programming' },
  { value: 'implement-lexer', label: 'Implement (part of) a lexer', group: 'Programming', parent: 'implement-parser' },
  { value: 'extend-language', label: 'Extend the language', group: 'Programming', parent: 'implement-interpreter' },
  { value: 'write-test-cases', label: 'Write test cases', group: 'Programming' },

  { value: 'trace-evaluation', label: 'Trace evaluation step by step', group: 'Understanding' },
  { value: 'reason-about-termination', label: 'Reason about termination', group: 'Understanding' },
  { value: 'reason-about-scoping', label: 'Reason about scoping and shadowing', group: 'Understanding' },
  { value: 'understand-partial-application', label: 'Understand partial application', group: 'Understanding' },
  { value: 'understand-evaluation-order', label: 'Understand evaluation order', group: 'Understanding' },
  { value: 'understand-environments', label: 'Understand environments and closures', group: 'Understanding' },
  { value: 'understand-memory-layout', label: 'Understand memory layout', group: 'Understanding' },
  { value: 'distinguish-syntax-semantics', label: 'Distinguish syntax and semantics', group: 'Understanding' },
  { value: 'read-grammars', label: 'Read and apply grammars', group: 'Understanding' },
  { value: 'build-syntax-tree', label: 'Build syntax trees', group: 'Understanding' },

  { value: 'prove-by-induction', label: 'Prove a statement by induction', group: 'Proving' },
  { value: 'prove-correctness', label: 'Prove correctness of a function', group: 'Proving' },
  { value: 'find-invariants', label: 'Find suitable invariants / strengthenings', group: 'Proving' },
  { value: 'formalise-statements', label: 'Formalise informal statements', group: 'Proving' },
  { value: 'reason-with-sets-relations', label: 'Reason about sets and relations', group: 'Proving' },

  { value: 'analyse-runtime', label: 'Analyse runtime', group: 'Analysis' },
  { value: 'use-big-o', label: 'Use O-notation correctly', group: 'Analysis' },
  { value: 'derive-cost-function', label: 'Derive cost and size functions', group: 'Analysis' },
  { value: 'compare-implementations', label: 'Compare implementations', group: 'Analysis' },
  { value: 'debug-code', label: 'Find and fix bugs', group: 'Analysis' },
  { value: 'review-solution', label: 'Review someone else’s solution', group: 'Analysis' },
  { value: 'build-derivation', label: 'Build a derivation', group: 'Analysis', parent: 'build-type-derivation' },
  { value: 'read-inference-rules', label: 'Read inference rules', group: 'Analysis' },
  { value: 'reason-about-complexity', label: 'Reason about complexity', group: 'Analysis', parent: 'analyse-runtime' },
  { value: 'understand-sorting-algorithms', label: 'Understand sorting algorithms', group: 'Analysis' },
  { value: 'understand-mutable-arrays', label: 'Understand mutable arrays', group: 'Analysis' },
  { value: 'understand-binary-search', label: 'Understand binary search', group: 'Analysis' },
  { value: 'reason-about-aliasing', label: 'Reason about aliasing', group: 'Analysis' },
  { value: 'apply-recurrence-theorems', label: 'Apply recurrence theorems', group: 'Analysis', parent: 'derive-cost-function' },
  { value: 'decompose-problem', label: 'Decompose a problem', group: 'Analysis' },
  { value: 'generalise-solution', label: 'Generalise a solution', group: 'Analysis' },
  { value: 'translate-between-notations', label: 'Translate between notations', group: 'Analysis' },
  { value: 'work-with-sets', label: 'Work with sets', group: 'Analysis', parent: 'reason-with-sets-relations' },
  { value: 'work-with-relations', label: 'Work with relations', group: 'Analysis', parent: 'reason-with-sets-relations' },
  { value: 'work-with-graphs', label: 'Work with graphs', group: 'Analysis' },

  { value: 'activate-prior-knowledge', label: 'Activate prior knowledge', group: 'Meta' },
  { value: 'self-assessment', label: 'Self-assessment', group: 'Meta' },
  { value: 'get-to-know-each-other', label: 'Get to know each other', group: 'Meta' },
  { value: 'exam-training', label: 'Train exam conditions', group: 'Meta' },
];

/* -------------------------------------------------------------- difficulty */

export const DIFFICULTY_RATED: TaxonomyEntry[] = [
  { value: '1', label: '1 — trivial', group: 'Our rating' },
  { value: '2', label: '2 — easy', group: 'Our rating' },
  { value: '3', label: '3 — medium', group: 'Our rating' },
  { value: '4', label: '4 — hard', group: 'Our rating' },
  { value: '5', label: '5 — very hard', group: 'Our rating' },
];

export const DIFFICULTY_SOURCE: TaxonomyEntry[] = [
  { value: '1', label: '🐪 one camel', group: 'Annotated' },
  { value: '2', label: '🐪🐪 two camels', group: 'Annotated' },
  { value: '3', label: '🐪🐪🐪 three camels', group: 'Annotated' },
  { value: '4', label: '🐪🐪🐪🐪 four camels', group: 'Annotated' },
];

/** Buckets used for the time-effort filter. */
export const TIME_BUCKETS = [
  { max: 5, label: '≤ 5 min' },
  { max: 10, label: '5–10 min' },
  { max: 20, label: '10–20 min' },
  { max: 30, label: '20–30 min' },
  { max: 45, label: '30–45 min' },
  { max: Infinity, label: '> 45 min' },
] as const;

export function timeBucketLabel(minutes: number): string {
  for (const b of TIME_BUCKETS) if (minutes <= b.max) return b.label;
  return TIME_BUCKETS[TIME_BUCKETS.length - 1].label;
}


/* ----------------------------------------------------------------- aliases */

/**
 * Values that mean the same thing as a canonical tag, or are too rare to earn a
 * facet row of their own, folded onto the tag that already covers them.
 *
 * Applied when tags are loaded (`normaliseTag`), so the database only ever holds
 * canonical values, and again when a query arrives, so an old bookmarked filter
 * URL keeps working.  Deliberately *not* a general stemmer: each entry is a
 * judgement about this corpus, and several were checked against the exercises
 * that carry the tag before being folded (`derivation` co-occurs with
 * `static-semantics` and `dynamic-semantics` and is the activity common to both,
 * so it stays; `binary` turned out to be a single binary-counter exercise).
 */
export type TagKind = 'topic' | 'type' | 'goal';

export const TAG_ALIASES: Record<TagKind, Record<string, string>> = {
  topic: {
    /* strict synonyms */
    arrays: 'vectors-arrays',
    matrices: 'vectors-arrays',
    exceptions: 'options-exceptions',
    'option-type': 'options-exceptions',
    sets: 'sets-relations',
    relations: 'sets-relations',
    'set-operations': 'sets-relations',
    'set-representation': 'sets-relations',
    'finitary-sets': 'sets-relations',
    'relation-properties': 'sets-relations',
    lexer: 'tokenization',
    'lexical-syntax': 'tokenization',
    'recursion-relation': 'recurrences',
    'state-references': 'references-mutability',
    'key-value-maps': 'association-lists',
    hashing: 'association-lists',
    'lambda-notation': 'lambda-abstraction',
    'lambda-calculus': 'lambda-abstraction',
    'beta-reduction': 'lambda-abstraction',
    combinators: 'higher-order-functions',
    'function-composition': 'higher-order-functions',
    memoization: 'dynamic-programming',
    logic: 'boolean-logic',
    loops: 'imperative-programming',
    sequencing: 'imperative-programming',
    'complete-induction': 'induction',
    'proof-technique': 'correctness-proofs',
    'derived-syntax': 'abstract-syntax',
    projections: 'tuples',
    'pairs-encoding': 'tuples',
    /* one-offs folded onto the tag that covers them */
    binary: 'numbers-arithmetic',
    'algebraic-properties': 'math-functions',
    'recursion-trees': 'cost-functions',
    'greedy-algorithms': 'dynamic-programming',
    exhaustiveness: 'pattern-matching',
    'block-representation': 'memory-heap',
    'tree-matching': 'trees',
    /* the existing `tree-traversal` label already says "traversal & addressing" */
    'tree-addressing': 'tree-traversal',
    'code-simplification': 'misc',
    randomness: 'misc',
    paradoxes: 'misc',
    nondeterminism: 'misc',
  },
  type: {
    gamified: 'gamification',
    'code-golf': 'puzzle',
    /* the sheets' own "exam prep" marking is a topic, not a format */
    'exam-prep': 'bonus',
  },
  goal: {
    'reason-about-efficiency': 'reason-about-complexity',
    'reason-about-polymorphism': 'understand-polymorphism',
    'reason-about-propositions': 'formalise-statements',
    'reason-about-algebraic-structure': 'formalise-statements',
    'reason-about-extensionality': 'formalise-statements',
    'reason-about-foundations': 'formalise-statements',
    counterexample: 'formalise-statements',
    'reduce-lambda-terms': 'trace-evaluation',
    'simplify-code': 'compare-implementations',
    'understand-hashing': 'work-with-lists',
    'use-vectors': 'understand-mutable-arrays',
    'understand-divide-and-conquer': 'understand-sorting-algorithms',
    'understand-application-syntax': 'distinguish-syntax-semantics',
    'reading-comprehension': 'review-solution',
  },
};

/**
 * The canonical form of a tag value.
 *
 * Keyed by kind, because the three vocabularies are separate namespaces that do
 * share spellings: `exam-prep` is a real *topic* and a stray *type*,
 * `counterexample` and `reading-comprehension` are real *types* and stray
 * *goals*.  A single flat alias table silently broke all three.
 */
export function normaliseTag(kind: TagKind, value: string): string {
  const v = value.trim();
  return TAG_ALIASES[kind]?.[v] ?? v;
}

/* -------------------------------------------------------------- hierarchy */

const childrenOf = (entries: TaxonomyEntry[]) => {
  const map = new Map<string, string[]>();
  for (const e of entries) {
    if (!e.parent) continue;
    map.set(e.parent, [...(map.get(e.parent) ?? []), e.value]);
  }
  return map;
};

export const TOPIC_CHILDREN = childrenOf(TOPICS);
export const TYPE_CHILDREN = childrenOf(EXERCISE_TYPES);
export const GOAL_CHILDREN = childrenOf(LEARNING_GOALS);

/**
 * A tag plus every tag that refines it, transitively.  Filtering by `trees` has
 * to find the exercises tagged only `tree-fold`, or the parent row in the
 * sidebar would show a count nobody can reach.
 */
export function withDescendants(value: string, children: Map<string, string[]>): string[] {
  const out = [value];
  for (let i = 0; i < out.length; i++) {
    for (const child of children.get(out[i]) ?? []) if (!out.includes(child)) out.push(child);
  }
  return out;
}

/** Entries in sidebar order: each parent immediately followed by its children. */
export function nested(entries: TaxonomyEntry[]): TaxonomyEntry[] {
  const children = childrenOf(entries);
  const byValue = new Map(entries.map((e) => [e.value, e]));
  const out: TaxonomyEntry[] = [];
  const push = (e: TaxonomyEntry) => {
    out.push(e);
    for (const c of children.get(e.value) ?? []) {
      const child = byValue.get(c);
      if (child) push(child);
    }
  };
  for (const e of entries) if (!e.parent) push(e);
  // a child whose parent is missing would otherwise vanish from the sidebar
  for (const e of entries) if (!out.includes(e)) out.push(e);
  return out;
}

/* ------------------------------------------------------------------ lookup */

const index = (entries: TaxonomyEntry[]) => new Map(entries.map((e) => [e.value, e]));

export const TOPIC_INDEX = index(TOPICS);
export const TYPE_INDEX = index(EXERCISE_TYPES);
export const GOAL_INDEX = index(LEARNING_GOALS);

/**
 * The names shown for tags in another interface language (Phase 7: the client's German).
 * The stored values never change; only the words on screen. Keys are `topic:<value>`,
 * `type:<value>`, `goal:<value>` and `group:<group>`; null (the default, and always on
 * the server) shows the English names above.
 */
let translation: ((key: string) => string | undefined) | null = null;
export function setTaxonomyTranslation(lookup: ((key: string) => string | undefined) | null): void {
  translation = lookup;
}

export function topicLabel(value: string): string {
  return translation?.(`topic:${value}`) ?? TOPIC_INDEX.get(value)?.label ?? humanise(value);
}
export function typeLabel(value: string): string {
  return translation?.(`type:${value}`) ?? TYPE_INDEX.get(value)?.label ?? humanise(value);
}
export function goalLabel(value: string): string {
  return translation?.(`goal:${value}`) ?? GOAL_INDEX.get(value)?.label ?? humanise(value);
}
/** A group of the filter sidebar and the tag picker, in the interface language. */
export function taxonomyGroupLabel(group: string): string {
  return translation?.(`group:${group}`) ?? group;
}
export function humanise(value: string): string {
  return value.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Groups in sidebar order. */
export function groupsOf(entries: TaxonomyEntry[]): string[] {
  const seen: string[] = [];
  for (const e of entries) if (!seen.includes(e.group)) seen.push(e.group);
  return seen;
}
